/**
 * Gestor de participantes nominales (modo uneven / categoría / waitlist).
 *
 * - Tarjetas tonificadas según estado (paid → olive, espera → ámbar).
 * - Copiar enlace personal + WhatsApp con texto preformateado.
 * - Form inline para añadir uno a mano.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '../api/client';
import toast from 'react-hot-toast';


interface Participant {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  custom_amount: number;
  category: string | null;
  is_waitlist: boolean;
  status: string;
  payment_url: string;
}

interface Props {
  slug: string;
  adminToken: string;
  refreshKey?: number;
}


function formatEUR(n: number): string {
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}


export default function ParticipantsManager({ slug, adminToken, refreshKey = 0 }: Props) {
  const [list, setList] = useState<Participant[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const [newName, setNewName] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newWaitlist, setNewWaitlist] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    api
      .get(`/public/group-buys/admin/${slug}/participants?t=${adminToken}`)
      .then((r) => setList(r.data.participants))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [slug, adminToken]);

  useEffect(() => {
    refresh();
  }, [refresh, refreshKey]);

  const addOne = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName || !newAmount) {
      toast.error('Faltan nombre e importe');
      return;
    }
    try {
      await api.post(`/public/group-buys/admin/${slug}/participants?t=${adminToken}`, {
        participants: [
          {
            name: newName,
            custom_amount: parseFloat(newAmount),
            email: newEmail || undefined,
            phone: newPhone || undefined,
            category: newCategory || undefined,
            is_waitlist: newWaitlist,
          },
        ],
      });
      toast.success('Añadido');
      setNewName('');
      setNewAmount('');
      setNewEmail('');
      setNewPhone('');
      setNewCategory('');
      setNewWaitlist(false);
      setAdding(false);
      refresh();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Enlace copiado al portapapeles');
  };

  const waText = (p: Participant) => {
    const url = `${window.location.origin}${p.payment_url}`;
    return encodeURIComponent(
      `Hola ${p.name}\n\nTe toca poner tu parte: *${formatEUR(p.custom_amount)}*\n\nPaga aquí (sin login):\n${url}\n\nGracias.`,
    );
  };

  if (loading && !list) return null;

  const paid = list?.filter((p) => p.status === 'paid').length || 0;
  const total = list?.length || 0;
  const totalAmount = list?.reduce((s, p) => s + p.custom_amount, 0) || 0;
  const paidAmount =
    list?.filter((p) => p.status === 'paid').reduce((s, p) => s + p.custom_amount, 0) || 0;

  return (
    <section className="bg-paper border border-line shadow-card rounded-card p-6 md:p-7">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-1">
            · Lista de participantes
          </div>
          <h2 className="font-display font-semibold text-[22px] text-ink tracking-[-0.01em]">
            Quién, cuánto, y por dónde le llega.
          </h2>
        </div>
        {total > 0 && (
          <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-500">
            <strong className="text-olive-700">{paid}</strong>/{total} pagados ·{' '}
            <strong className="text-olive-700">{formatEUR(paidAmount)}</strong> de{' '}
            {formatEUR(totalAmount)}
          </div>
        )}
      </div>

      {list && list.length === 0 && !adding ? (
        <div className="border-[1.5px] border-dashed border-line-strong rounded-card p-8 text-center">
          <p className="text-[14.5px] text-ink-700 mb-4">
            Aún no hay participantes nominales.
          </p>
          <button
            onClick={() => setAdding(true)}
            className="px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[14px] shadow-btn hover:bg-brand-700 transition"
          >
            + Añadir uno a mano
          </button>
          <p className="text-[12px] text-ink-500 mt-3">
            O sube una foto del ticket arriba y se añaden en bloque.
          </p>
        </div>
      ) : (
        <ul className="space-y-2 mb-4">
          {list?.map((p) => (
            <ParticipantRow
              key={p.id}
              p={p}
              onCopy={() => copy(`${window.location.origin}${p.payment_url}`)}
              waText={waText(p)}
            />
          ))}
        </ul>
      )}

      {/* Form añadir uno */}
      {adding ? (
        <form
          onSubmit={addOne}
          className="border-t border-line-soft pt-5 mt-3 space-y-3 bg-surface -mx-6 md:-mx-7 px-6 md:px-7 pb-5 rounded-b-card"
        >
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500">
            · Nuevo participante
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nombre *"
              required
              className="cc-input"
            />
            <input
              type="number"
              step="0.01"
              value={newAmount}
              onChange={(e) => setNewAmount(e.target.value)}
              placeholder="Importe (€) *"
              required
              className="cc-input"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Email (opcional)"
              className="cc-input"
            />
            <input
              type="tel"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="Teléfono (para WhatsApp)"
              className="cc-input"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-center">
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Categoría (niños, adultos…)"
              className="cc-input"
            />
            <label className="flex items-center gap-2 text-[13.5px] text-ink-700">
              <input
                type="checkbox"
                checked={newWaitlist}
                onChange={(e) => setNewWaitlist(e.target.checked)}
                className="w-4 h-4 accent-brand"
              />
              Lista de espera
            </label>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="px-4 py-2.5 rounded-xl border-[1.5px] border-line-strong bg-paper text-ink-700 text-[13.5px] font-semibold hover:border-brand transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 rounded-xl bg-brand text-paper text-[13.5px] font-semibold shadow-btn hover:bg-brand-700 transition"
            >
              Añadir
            </button>
          </div>
        </form>
      ) : (
        list &&
        list.length > 0 && (
          <button
            onClick={() => setAdding(true)}
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand hover:text-brand-700 transition"
          >
            + Añadir uno a mano
          </button>
        )
      )}

      <Style />
    </section>
  );
}


function ParticipantRow({
  p,
  onCopy,
  waText,
}: {
  p: Participant;
  onCopy: () => void;
  waText: string;
}) {
  const tone =
    p.status === 'paid'
      ? { bg: '#E8EEDC', border: '#C7D5A8', text: '#46602C' }
      : p.is_waitlist
      ? { bg: '#F7EBD2', border: '#ECD9AE', text: '#8A5F12' }
      : { bg: '#FBF7F0', border: '#EAE1D2', text: '#26201A' };

  return (
    <li
      className="rounded-xl px-4 py-3 flex flex-col md:flex-row md:items-center gap-3"
      style={{ background: tone.bg, border: `1px solid ${tone.border}` }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p
            className="font-display font-semibold text-[15.5px] tracking-[-0.01em] truncate"
            style={{ color: tone.text }}
          >
            {p.name}
          </p>
          {p.category && (
            <span className="text-[11px] px-2 py-0.5 rounded-pill bg-paper border border-line text-ink-700 font-semibold">
              {p.category}
            </span>
          )}
          {p.is_waitlist && (
            <span className="text-[11px] px-2 py-0.5 rounded-pill bg-amberCC-50 text-amberCC-700 font-semibold border border-[#ECD9AE]">
              · en espera
            </span>
          )}
          {p.status === 'paid' && (
            <span className="text-[11px] font-mono uppercase tracking-[0.14em] text-olive-700">
              ✓ pagado
            </span>
          )}
        </div>
        {p.email && (
          <p className="text-[12px] text-ink-500 truncate mt-0.5">{p.email}</p>
        )}
      </div>
      <div className="text-right">
        <p
          className="font-display font-bold text-[18px] tracking-[-0.01em] leading-none"
          style={{ color: tone.text }}
        >
          {formatEUR(p.custom_amount)}
        </p>
      </div>
      {p.status !== 'paid' && (
        <div className="flex gap-2">
          <button
            onClick={onCopy}
            className="px-3 py-1.5 rounded-pill border-[1.5px] border-line-strong bg-paper text-ink-700 text-[12px] font-semibold hover:border-brand transition"
            title="Copiar enlace de pago"
          >
            Copiar
          </button>
          {p.phone && (
            <a
              href={`https://wa.me/${p.phone.replace(/\D/g, '')}?text=${waText}`}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-1.5 rounded-pill border-[1.5px] border-olive bg-olive-50 text-olive-700 text-[12px] font-semibold hover:bg-[#D8E2C2] transition"
              title="Enviar por WhatsApp"
            >
              WhatsApp
            </a>
          )}
        </div>
      )}
    </li>
  );
}


function Style() {
  return (
    <style>{`
      .cc-input{
        width:100%;
        padding:11px 14px;
        border:1.5px solid #DDD3C4;
        border-radius:11px;
        background:#FFFFFF;
        font-family:inherit;
        font-size:13.5px;
        color:#26201A;
        outline:none;
        transition:border-color .15s ease;
      }
      .cc-input:focus{ border-color:#1C5D82; }
      .cc-input::placeholder{ color:#A89D8E; }
    `}</style>
  );
}
